import { Page } from "./page/page"
import { UndoRedoManager } from '@/utilityModels/undoRedoManager';

import * as def from '@/definitions'
import { ArrayHelper } from '@/helper/arrayHelper';
import { Product } from '@/data/productCategory/product/product';
import { Paper } from '@/data/productCategory/product/paper/paper';
import { Frame } from './frame/frame';
import { InsertionData } from './insertionData/insertionData';
import { Const } from '@/const';
import { Design } from './page/surface/design/design';
import { Layer } from './page/surface/design/layer/layer';
import { PlainTextLayer } from './page/surface/design/layer/plainTextLayer';
import { UuidHelper } from '@/helper/uuidHelper';
import { CircleTextLayer } from './page/surface/design/layer/circleTextLayer';
import { QRcodeLayer } from './page/surface/design/layer/qrcodeLayer';
import { BarcodeLayer } from './page/surface/design/layer/barcodeLayer';
import { NumberCoreLayer, NumberLayer } from './page/surface/design/layer/numberLayer';
import { NameHelper } from '@/helper/nameHelper';
import { InsertionHelper } from '@/helper/insertionHelper';
import { FileHelper } from '@/helper/fileHelper';
import { EncryptionHelper } from '@/helper/encryptionHelper';
import { DebugHelper } from '@/helper/debugHelper';

/** 印刷ファイル */

export class Document
{
    // ファイルバージョン(APP固有)
    private _fileVersion : number = Const.FILE_VERSION;
    public get fileVersion() { return this._fileVersion; }
    public set fileVersion(fileVersion) { this._fileVersion = fileVersion; }

    // ファイル作成環境(APP固有)
    private _fileEnvironment : string = Const.FILE_ENVIRONMENT;
    public get fileEnvironment() { return this._fileEnvironment; }
    public set fileEnvironment(fileEnvironment) { this._fileEnvironment = fileEnvironment; }

    // Label9ファイルバージョン
    private _fileVersionLabel9 : string = "";
    public get fileVersionLabel9() { return this._fileVersionLabel9; }
    public set fileVersionLabel9(fileVersionLabel9) { this._fileVersionLabel9 = fileVersionLabel9; }

    // Label9の文字のうち、再現できなかったフォント
    private _unloadedFonts : Array<string> = [];
    public get unloadedFonts() { return this._unloadedFonts; }
    public set unloadedFonts(unloadedFonts) { this._unloadedFonts = unloadedFonts; }

    // Label9コンバート時の背景情報
    // 0 : 背景なし
    // 1 : 背景色あり
    // 2 : 背景画像あり
    private _backgroundTypeLabel9 : number = 0;
    public get backgroundTypeLabel9() { return this._backgroundTypeLabel9; }
    public set backgroundTypeLabel9(backgroundTypeLabel9) { this._backgroundTypeLabel9 = backgroundTypeLabel9; }

    // 前回開いた時のファイル名
    private _openedFileName : string = "";
    public get openedFileName() { return this._openedFileName; }
    public set openedFileName(openedFileName) { this._openedFileName = openedFileName; }

    // ファイルパス
    //* DLで上書き保存の判定のために使用する
    //* ファイル開く、別名保存のときに更新する
    //* セキュリティ上保存対象としない
    private _currentFilePath : string = "";
    public get currentFilePath() { return this._currentFilePath; }
    public set currentFilePath(currentFilePath) { this._currentFilePath = currentFilePath; }

    // カテゴリーID
    private _productCategoryId : number = 0;
    public get productCategoryId() { return this._productCategoryId; }
    public set productCategoryId(productCategoryId) { this._productCategoryId = productCategoryId; }

    // 製品コード
    private _productCode : string = "";
    public get productCode() { return this._productCode; }
    public set productCode(productCode) { this._productCode = productCode; }

    // 製品ID
    private _productId : number = 0;
    public get productId() { return this._productId; }
    public set productId(productId) { this._productId = productId; }

    // 製品名
    private _productName : string = "";
    public get productName() { return this._productName; }
    public set productName(productName) { this._productName = productName; }

    // 国コード
    private _productRegion : string = "";
    public get productRegion() { return this._productRegion; }
    public set productRegion(productRegion) { this._productRegion = productRegion; }

    // 用紙コード
    private _paperCode : string = "";
    public get paperCode() { return this._paperCode; }
    public set paperCode(paperCode) { this._paperCode = paperCode; }

    // 用紙フォーマットコード
    private _paperFormatCode : string = "";
    public get paperFormatCode() { return this._paperFormatCode; }
    public set paperFormatCode(paperFormatCode) { this._paperFormatCode = paperFormatCode; }

    // 面の数
    private _surfaceNumber : number = 1;
    public get surfaceNumber() { return this._surfaceNumber; }
    public set surfaceNumber(surfaceNumber) { this._surfaceNumber = surfaceNumber; }

    // フレーム数
    private _frameNumber : number = 1;
    public get frameNumber() { return this._frameNumber; }
    public set frameNumber(frameNumber) { this._frameNumber = frameNumber; }

    // 用紙の回転
    private _rotate : number = 0;
    public get rotate() { return this._rotate; }
    public set rotate(rotate) { this._rotate = rotate; }

    // ページ幅
    private _pageWidth : number = 210.0;
    public get pageWidth() { return this._pageWidth; }
    public set pageWidth(pageWidth) { this._pageWidth = pageWidth; }

    // ページ高さ
    private _pageHeight : number = 297.0;
    public get pageHeight() { return this._pageHeight; }
    public set pageHeight(pageHeight) { this._pageHeight = pageHeight; }

    // フレーム
    private _frameArray : Array<Frame> = new Array();
    public get frameArray() { return this._frameArray; }
    public set frameArray(frameArray) { this._frameArray = frameArray; }

    // ページリスト
    private _pageList : Page[] = new Array();
    public get pageList() { return this._pageList; }
    public get pageCount() { return this.pageList.length; }
    public set pageList(pageList) { this._pageList = pageList; }
    public addPage(page) { this._pageList = ArrayHelper.add(this._pageList, page); }
    public insertPage(index, page) { this._pageList = ArrayHelper.insert(this._pageList, page, index); }
    public removePage(index) { this._pageList = ArrayHelper.removeAt(this._pageList, index); }
    
    // 空のページを作成
    public createNewPage() : Page { return Page.create(this.pageWidth, this.pageHeight, this.frameArray, this.hasReverseSurface); }

    // カレントページインデックス
    private _currentPageIndex : number = 0;
    public get currentPageIndex() { return this._currentPageIndex; }
    public set currentPageIndex(currentPageIndex : number) { this._currentPageIndex = currentPageIndex; }

    // カレント面インデックス
    private _currentSurfaceIndex : number = 0;
    // 裏面を持つかどうか
    public get hasReverseSurface() { return this.surfaceNumber >= 2; }
    // 裏面かどうか
    public get isReverseSurface() { return this._currentSurfaceIndex == 1; }
    // 面を表・裏に設定
    public setSurface(isReverse = false) { this._currentSurfaceIndex = isReverse ? 1 : 0; }

    // オブジェクトが存在するかどうか
    public hasAnyObject(isGroupLayerContained = true) : boolean
    {
        var result = false;
        var isBreak = false;
        for(var pageIndex = 0; pageIndex < this.pageCount; ++pageIndex)
        {
            var page = this._pageList[pageIndex];
            for(var surfaceIndex = 0; surfaceIndex < (page.hasReverse ? 2 : 1); ++surfaceIndex)
            {
                var surface = page.getSurface(surfaceIndex == 1);
                var designNumber = this.useInsertion ? 1 : page.designMaxNumber;
                for(var pos = 0; pos < designNumber; ++pos)
                {
                    var design = surface.getDesign(pos);
                    if(design != null)
                    {
                        if(design.hasAnyObject(isGroupLayerContained))
                        {
                            result = true;
                            isBreak = true;
                            break;
                        }
                    }
                }
                if(isBreak) { break; }
            }
            if(isBreak) { break; }
        }

        return result;
    }

    // 指定のpositionと同じ横列となるpositionを配列で取得する
    public getHorizontalPositionArray(position : number)
    {
        var isFound = false;
        var array : Array<number> = new Array();
        var totalPosition = 0;
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            for(var row = 0; row < frame.row; ++row)
            {
                for(var col = 0; col < frame.col; ++col)
                {
                    // 配列追加
                    array.push(totalPosition);
                    // positionのカレント確認
                    if(position == totalPosition)
                    {
                        isFound = true;
                    }
                    totalPosition++;
                }
                if(isFound)
                {
                    // 見つかった
                    break;
                }
                
                array = new Array();
            }
            if(isFound)
            {
                break;
            }
        }

        return isFound ? array : new Array();
    }

    // 指定のpositionと同じ縦列となるpositionを配列で取得する
    public getVerticalPositionArray(position : number)
    {
        var isFound = false;
        var targetCol = -1;
        var totalCol = -1;
        var array : Array<number> = new Array();
        var totalPosition = 0;
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            for(var row = 0; row < frame.row; ++row)
            {
                for(var col = 0; col < frame.col; ++col)
                {
                    // 配列追加
                    array.push(totalPosition);
                    // positionのカレント確認
                    if(position == totalPosition)
                    {
                        isFound = true;
                        targetCol = col;
                        totalCol = frame.col;
                    }
                    totalPosition++;
                }
            }
            if(isFound)
            {
                break;
            }
            array = new Array();
        }

        var newArray = new Array();
        var countCol = 0;
        for(var i = 0; i < array.length; ++i)
        {
            if(countCol == targetCol)
            {
                newArray.push(array[i]);
            }
            countCol++;
            if(countCol >= totalCol)
            {
                countCol = 0;
            }
        }

        return isFound ? newArray : new Array();
    }

    // 指定のpositionと同じフレームとなるpositionを配列で取得する
    public getFramePositionArray(position : number)
    {
        var isFound = false;
        var array : Array<number> = new Array();
        var totalPosition = 0;
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            for(var row = 0; row < frame.row; ++row)
            {
                for(var col = 0; col < frame.col; ++col)
                {
                    // 配列追加
                    array.push(totalPosition);
                    // positionのカレント確認
                    if(position == totalPosition)
                    {
                        isFound = true;
                    }
                    totalPosition++;
                }
            }
            if(isFound)
            {
                // 見つかった
                break;
            }
            array = new Array();
        }

        return isFound ? array : new Array();
    }

    // ドキュメントの回転
    public rotateDocument(delta : number)
    {
        var isRotatedDesign = false;

        // 計算の簡単のため、回転角を0以上、360未満に収める
        while(delta < 0) { delta += 360; }
        while(delta >= 360) { delta -= 360; }

        if(delta > 180)
        {
            // 90度回転を複数回行う
            var rotateCount = Math.floor(delta / 90);
            for(var j = 0; j < rotateCount; ++j)
            {
                this.rotateDocument(90);
            }
            return;
        }

        // 新しい回転角
        var newDocumentRotate = this.rotate + delta;
        while(newDocumentRotate < 0) { newDocumentRotate += 360; }
        while(newDocumentRotate >= 360) { newDocumentRotate -= 360; }

        // 全ページに対して処理を行う
        for(var pageIndex = 0; pageIndex < this._pageList.length; ++pageIndex)
        {
            var page = this._pageList[pageIndex];
            // 新しいページサイズ
            var newPageWidth = (delta == 90) ? page.height : page.width;
            var newPageHeight = (delta == 90) ? page.width : page.height;
            
            // 各面に対して処理を行う
            for(var surfaceIndex = 0; surfaceIndex < page.surfaces.length; ++surfaceIndex)
            {
                var surface = page.surfaces[surfaceIndex];
                // 全デザインに対して処理を行う
                for(var i = 0; i < page.designMaxNumber; ++i)
                {
                    var design = surface.getDesign(i);
                    if(design == null) { continue; }

                    // デザインの回転
                    var orgRotate = design.rotate;
                    // NumberLayerは最初のデザインだけ回転させる
                    var rotateNumberLayer = !isRotatedDesign;
                    design.rotateDesign(delta, rotateNumberLayer);
                    // デザインの角度の数値までは変更させないので、元に戻す
                    design.rotate = orgRotate;
                    // 回転済みフラグ
                    isRotatedDesign = true;
                }

                // 面サイズ更新
                surface.width = newPageWidth;
                surface.height = newPageHeight;
            }

            // ページサイズ更新
            page.width = newPageWidth;
            page.height = newPageHeight;
        }

        // フレーム情報の回転
        for(var i = 0; i < this.frameArray.length; ++i)
        {
            var frame = this.frameArray[i];
            if(delta == 90)
            {
                // 行列の逆転
                var oldCol = frame.col;
                var oldRow = frame.row;
                frame.col = oldRow;
                frame.row = oldCol;

                // paddingの逆転
                var oldHorizontalPadding = frame.horizontalPadding;
                var oldVerticalPadding = frame.verticalPadding;
                frame.horizontalPadding = oldVerticalPadding;
                frame.verticalPadding = oldHorizontalPadding;

                // 幅・高さの逆転
                var oldW = frame.width;
                var oldH = frame.height;
                frame.width = oldH;
                frame.height = oldW;

                // marginの変更
                var oldTopMargin = frame.topMargin;
                var oldLeftMargin = frame.leftMargin;
                frame.topMargin = oldLeftMargin;
                frame.leftMargin = this.pageHeight - oldTopMargin - oldH * oldRow - oldVerticalPadding * (oldRow - 1);
            }
        }
        
        // ドキュメントの回転角更新
        this.rotate = newDocumentRotate;
        // 必要があれば用紙サイズ回転
        if(delta == 90)
        {
            var oldPageWidth = this.pageWidth;
            var oldPageHeight = this.pageHeight;
            this.pageWidth = oldPageHeight;
            this.pageHeight = oldPageWidth;
        }

        // surfaceのフレーム情報が更新されないことがあるので再設定
        // 全ページに対して処理を行う
        for(var pageIndex = 0; pageIndex < this._pageList.length; ++pageIndex)
        {
            var page = this._pageList[pageIndex];
            // 各面に対して処理を行う
            for(var surfaceIndex = 0; surfaceIndex < page.surfaces.length; ++surfaceIndex)
            {
                var surface = page.surfaces[surfaceIndex];

                surface.frameArray = this.frameArray;
            }
        }
    }

    // アンドゥリドゥ管理クラス
    public undoRedoManager : UndoRedoManager = new UndoRedoManager();

    // 差し込み関連
    // 差し込みを使用しているか
    private _useInsertion : boolean = false;
    public get useInsertion() { return this._useInsertion; }
    public set useInsertion(useInsertion) { this._useInsertion = useInsertion; }

    // 差し込みデータ
    private _insertionData : InsertionData = new InsertionData();
    public get insertionData() { return this._insertionData; }
    public set insertionData(insertionData) { this._insertionData = insertionData; }

    // 選択情報
    public selectedInsertionRow = 0;
    public selectedInsertionColumn = 0;
    public selectedInsertionWholeColumn = -1;

    // 連番コアレイヤー
    private _numberLayers : Array<NumberCoreLayer> = new Array();
    public get numberLayers() { return this._numberLayers; }
    public set numberLayers(numberLayers) { this._numberLayers = numberLayers; }

    // 削除した連番コアレイヤー
    // undoのときに復帰するための処置、ファイルには保存しない
    private numberLayersRemoved : Array<NumberCoreLayer> = new Array();

    // 特定のsurfaceIndexに置かれる連番レイヤーをすべて得る
    public getNumberLayersBySurfaceIndex(surfaceIndex) : Array<NumberCoreLayer>
    {
        var array : Array<NumberCoreLayer> = new Array();
        for(var i = 0; i < this._numberLayers.length; ++i)
        {
            if(this._numberLayers[i].surfaceIndex == surfaceIndex)
            {
                array.push(this._numberLayers[i]);
            }
        }
        return array;
    }

    // 連番レイヤー追加後のdoumentへの処置
    public updateDesignAfterAddNumberLayer(layer : Layer)
    {
        if(!(layer instanceof NumberLayer)) { return; }

        // 削除済みの場合復帰する
        var foundIndex = -1;
        for(var i = 0; i < this.numberLayersRemoved.length; ++i)
        {
            var removed = this.numberLayersRemoved[i];
            if(removed.linkId == layer.linkId)
            {
                foundIndex = i;
                break;
            }
        }
        if(foundIndex >= 0)
        {
            var found = this.numberLayersRemoved[foundIndex];
            this.numberLayersRemoved.splice(foundIndex, 1);
            this.numberLayers.push(found);
        }

        // 元となるコアレイヤーの取得
        var coreLayer : NumberCoreLayer | null = null;
        for(var i = 0; i < this.numberLayers.length; ++i)
        {
            if(this.numberLayers[i].linkId == layer.linkId)
            {
                coreLayer = this.numberLayers[i];
                break;
            }
        }

        // コアレイヤーが無い場合は何もしない
        if(coreLayer == null) { return; }

        // すべてのデザインに対してレイヤーを作成して追加する
        var surfaceIndex = layer.surfaceIndex;
        for(var pageIndex = 0; pageIndex < this.pageCount; ++pageIndex)
        {
            var page = this._pageList[pageIndex];
            var surface = page.getSurface(surfaceIndex > 0);
            for(var designIndex = 0; designIndex < page.designMaxNumber; ++designIndex)
            {
                var design = surface.getDesign(designIndex);
                if(design != null)
                {
                    var allLayers = design.getAllLayers(false);

                    // すべてのNumberLayerを調べ、コアレイヤーと同じlinkIdを持つレイヤーが無い場合は追加する。
                    var isContained = false;
                    for(var layerIndex = 0; layerIndex < allLayers.length; ++layerIndex)
                    {
                        var targetLayer = allLayers[layerIndex];
                        if(targetLayer instanceof NumberLayer)
                        {
                            if(coreLayer != null && targetLayer.linkId == coreLayer.linkId)
                            {
                                isContained = true;
                                break;
                            }
                        }
                    }
                    if(!isContained && coreLayer != null)
                    {
                        var newLayer = new NumberLayer();
                        newLayer.linkId = coreLayer.linkId;
                        newLayer.document = this;
                        newLayer.surfaceIndex = surfaceIndex;
                        newLayer.name = NameHelper.createLayerName("number", design);
                        design.layer.addLayer(newLayer);
                    }

                }
            }
        }
    }

    // 連番レイヤー削除後のdoumentへの処置
    public updateDesignAfterRemoveNumberLayer(layer : Layer)
    {
        if(!(layer instanceof NumberLayer)) { return; }

        var surfaceIndex = layer.surfaceIndex;

        // コアを削除する
        var foundIndex = -1;
        for(var i = 0; i < this.numberLayers.length; ++i)
        {
            var added = this.numberLayers[i];
            if(added.linkId == layer.linkId)
            {
                foundIndex = i;
                break;
            }
        }
        if(foundIndex >= 0)
        {
            var found = this.numberLayers[foundIndex];
            this.numberLayers.splice(foundIndex, 1);
            this.numberLayersRemoved.push(found);
        }

        var linkId = layer.linkId;

        // すべてのデザインに対してレイヤーを削除する
        for(var pageIndex = 0; pageIndex < this.pageCount; ++pageIndex)
        {
            var page = this._pageList[pageIndex];
            var surface = page.getSurface(surfaceIndex > 0);
            for(var designIndex = 0; designIndex < page.designMaxNumber; ++designIndex)
            {
                var design = surface.getDesign(designIndex);
                if(design != null)
                {
                    var allLayers = design.getAllLayers(false);

                    // すべてのNumberLayerを調べ、コアレイヤーと同じlinkIdを持つレイヤーがある場合は削除する。
                    var removeLayer : Layer | null = null;
                    for(var layerIndex = 0; layerIndex < allLayers.length; ++layerIndex)
                    {
                        var targetLayer = allLayers[layerIndex];
                        if(targetLayer instanceof NumberLayer)
                        {
                            if(targetLayer.linkId == linkId)
                            {
                                removeLayer = targetLayer;
                                break;
                            }
                        }
                    }
                    if(removeLayer != null)
                    {
                        var parent = design.getParentLayer(removeLayer);
                        if(parent != null)
                        {
                            parent.removeLayer(removeLayer);
                        }
                    }
                }
            }
        }
    }

    // デザイン作成後のdocumentへの処置
    public updateDesignAfterAddDesign(design : Design, isReverse : boolean)
    {
        var surfaceIndex = isReverse ? 1 : 0;
        var coreLayers = this.getNumberLayersBySurfaceIndex(surfaceIndex);
        // ドキュメントにNumberLayerを作成する
        for(var i = 0; i < coreLayers.length; ++i)
        {
            var coreLayer = coreLayers[i];
            var isContained = false;
            for(var j = 0; j < design.getAllLayers(false).length; ++j)
            {
                var layer = design.getAllLayers(false)[j];
                if(layer instanceof NumberLayer && layer.linkId == coreLayer.linkId)
                {
                    isContained = true;
                    break;
                }
            }
            if(!isContained)
            {
                var newLayer = new NumberLayer();
                newLayer.linkId = coreLayer.linkId;
                newLayer.document = this;
                newLayer.surfaceIndex = surfaceIndex;
                newLayer.name = NameHelper.createLayerName("number", design);
                design.layer.addLayer(newLayer);
            }
        }
    }

    // 画像データ
    // レイヤーId -> データindex
    public layerImageMap : { [ id : string ] : number } = {};
    // 画像データの配列
    public layerImageData : Array<string> = [];
    public layerImageDataWhiteTransparent : Array<string> = [];
    public layerImageDataOrigin : Array<string> = [];
    public layerImageDataWhiteTransparentOrigin : Array<string> = [];

    // 既存のドキュメント同じ設定でドキュメントを作成する。
    public static createFromDocument(srcDocument : Document)
    {
        // 新規ドキュメント作成
        var document = new Document();

        // productの情報を適用
        document.productCategoryId = srcDocument.productCategoryId;
        document.productCode = srcDocument.productCode;
        document.productId = srcDocument.productId;
        document.productName = srcDocument.productName;
        document.productRegion = srcDocument.productRegion;

        // paperの情報を適用
        document.paperCode = srcDocument.paperCode;
        document.paperFormatCode = srcDocument.paperFormatCode;
        document.surfaceNumber = srcDocument.surfaceNumber;
        document.frameNumber = srcDocument.frameNumber;
        document.pageWidth = srcDocument.pageWidth;
        document.pageHeight = srcDocument.pageHeight;
        document.rotate = srcDocument.rotate;

        for(var i = 0; i < srcDocument.frameArray.length; ++i)
        {
            var frame = srcDocument.frameArray[i];
            document.frameArray.push(frame);
        }

        // 1ページ目を作る
        var page = Page.create(document.pageWidth, document.pageHeight, document.frameArray, document.hasReverseSurface);
        document.addPage(page);

        // 差し込みデータ初期作成
        document.insertionData = InsertionData.create();

        return document;
    }

    // 商品・用紙を元にしてドキュメントを作成する。
    public static create(product : Product, paper : Paper, rotate : number = 0)
    {
        // 新規ドキュメント作成
        var document = new Document();

        // productの情報を適用
        document.productCategoryId = product.productCategoryId;
        document.productCode = product.codeWeb;
        document.productId = product.id;
        document.productName = paper.name != "" ? paper.name : product.name;
        document.productRegion = product.region;

        // paperの情報を適用
        document.paperCode = paper.code;
        document.paperFormatCode = paper.formatCode;
        document.surfaceNumber = paper.surface;
        document.frameNumber = paper.frameNumber;
        document.pageWidth = paper.width;
        document.pageHeight = paper.height;

        // フレームの情報を適用
        if(paper.frameInfoList != null)
        {
            for(var frameInfo of paper.frameInfoList)
            {
                document.frameArray.push(Frame.createFromFrameInfo(frameInfo));
            }
        }

        // 1ページ目を作る
        var page = Page.create(document.pageWidth, document.pageHeight, document.frameArray, document.hasReverseSurface);
        document.addPage(page);

        // 差し込みデータ初期作成
        document.insertionData = InsertionData.create();

        // documentを初期回転する
        if(rotate != 0)
        {
            document.rotateDocument(rotate);
        }

        // この時点でのhashを記録する
        document.updateHash();

        return document;
    }

    // JSON
    public toJSON()
    {
        // JSONパース用オブジェクト
        var cloneObj = {
            fileVersion : Const.FILE_VERSION,                   // 定数を使い保存時のもので上書きする
            fileEnvironment : Const.FILE_ENVIRONMENT,
            fileVersionLabel9 : this._fileVersionLabel9,
            unloadedFonts : this._unloadedFonts,
            backgroundTypeLabel9 : this._backgroundTypeLabel9,
            openedFileName : this._openedFileName,
            productCategoryId : this._productCategoryId,
            productCode : this._productCode,
            productId : this._productId,
            productName : this._productName,
            productRegion : this._productRegion,
            paperCode : this._paperCode,
            paperFormatCode : this._paperFormatCode,
            surfaceNumber : this._surfaceNumber,
            frameNumber : this._frameNumber,
            rotate : this._rotate,
            pageWidth : this._pageWidth,
            pageHeight : this._pageHeight,
            pageList : this._pageList,
            frameArray : this._frameArray,
            useInsertion : this._useInsertion,
            insertionData : this._insertionData,
            numberLayers : this._numberLayers,
            layerImageMap : this.layerImageMap,
            layerImageData : this.layerImageData,
            layerImageDataWhiteTransparent : this.layerImageDataWhiteTransparent,
            layerImageDataOrigin : this.layerImageDataOrigin,
            layerImageDataWhiteTransparentOrigin : this.layerImageDataWhiteTransparentOrigin,
        };

        return cloneObj;
    }

    public static createFromJsonData(jsonData : any)
    {
        var document = new Document();
        if(jsonData.fileVersion != null) { document.fileVersion = jsonData.fileVersion; }
        if(jsonData.fileEnvironment != null) { document.fileEnvironment = jsonData.fileEnvironment; }
        if(jsonData.fileVersionLabel9 != null) { document.fileVersionLabel9 = jsonData.fileVersionLabel9; }
        if(jsonData.unloadedFonts != null)
        {
            for(var i = 0; i < jsonData.unloadedFonts.length; ++i)
            {
                document.unloadedFonts.push(jsonData.unloadedFonts[i]);
            }
        }
        if(jsonData.openedFileName != null) { document.openedFileName = jsonData.openedFileName; }
        if(jsonData.backgroundTypeLabel9) { document.backgroundTypeLabel9 = jsonData.backgroundTypeLabel9; }
        if(jsonData.productCategoryId != null) { document.productCategoryId = jsonData.productCategoryId; }
        if(jsonData.productCode != null) { document.productCode = jsonData.productCode; }
        if(jsonData.productId != null) { document.productId = jsonData.productId; }
        if(jsonData.productName != null) { document.productName = jsonData.productName; }
        if(jsonData.productRegion != null) { document.productRegion = jsonData.productRegion; }
        if(jsonData.paperCode != null) { document.paperCode = jsonData.paperCode; }
        if(jsonData.paperFormatCode != null) { document.paperFormatCode = jsonData.paperFormatCode; }
        if(jsonData.surfaceNumber != null) { document.surfaceNumber = jsonData.surfaceNumber; }
        if(jsonData.frameNumber != null) { document.frameNumber = jsonData.frameNumber; }
        if(jsonData.rotate != null) { document.rotate = jsonData.rotate; }
        if(jsonData.pageWidth != null) { document.pageWidth = jsonData.pageWidth; }
        if(jsonData.pageHeight != null) { document.pageHeight = jsonData.pageHeight; }
        if(jsonData.pageList != null)
        {
            for(var i = 0; i < jsonData.pageList.length; ++i)
            {
                document.pageList.push(Page.createFromJsonData(jsonData.pageList[i]));
            }
        }
        if(jsonData.frameArray != null)
        {
            for(var i = 0; i < jsonData.frameArray.length; ++i)
            {
                document.frameArray.push(Frame.createFromJsonData(jsonData.frameArray[i]));
            }
        }
        if(jsonData.useInsertion != null) { document.useInsertion = jsonData.useInsertion; }
        if(jsonData.insertionData != null) { document.insertionData = InsertionData.createFromJsonData(jsonData.insertionData); }
        if(jsonData.numberLayers != null) 
        {
            for(var i = 0; i < jsonData.numberLayers.length; ++i)
            {
                document.numberLayers.push(NumberCoreLayer.createFromJsonData(jsonData.numberLayers[i]));
            }
        }
        if(jsonData.layerImageMap != null) { document.layerImageMap = jsonData.layerImageMap; }
        if(jsonData.layerImageData != null) { document.layerImageData = jsonData.layerImageData; }
        if(jsonData.layerImageDataWhiteTransparent != null) { document.layerImageDataWhiteTransparent = jsonData.layerImageDataWhiteTransparent; }
        if(jsonData.layerImageDataOrigin != null) { document.layerImageDataOrigin = jsonData.layerImageDataOrigin; }
        if(jsonData.layerImageDataWhiteTransparentOrigin != null) { document.layerImageDataWhiteTransparentOrigin = jsonData.layerImageDataWhiteTransparentOrigin; }

        // documentに含まれるすべてのnumberLayerに参照を与える
        document.applyDocumentToNumberLayer();

        // この時点でのhashを記録する
        document.updateHash();

        return document;
    }

    public applyDocumentToNumberLayer()
    {
        for(var pageIndex = 0; pageIndex < this._pageList.length; ++pageIndex)
        {
            var page = this._pageList[pageIndex];
            for(var surfaceIndex = 0; surfaceIndex < page.surfaces.length; ++surfaceIndex)
            {
                var surface = page.surfaces[surfaceIndex];
                for(var designIndex = 0; designIndex < page.designMaxNumber; ++designIndex)
                {
                    var design = surface.getDesign(designIndex);
                    if(design != null)
                    {
                        var allLayers = design.getAllLayers(false);
                        for(var layerIndex = 0; layerIndex < allLayers.length; ++layerIndex)
                        {
                            var layer = allLayers[layerIndex];
                            if(layer instanceof NumberLayer)
                            {
                                layer.document = this;
                            }
                        }
                    }
                }
            }
        }
    }

    // エラーチェック
    public hasError()
    {
        var error = false;
        for(var i = 0; i < this._pageList.length; ++i)
        {
            var page = this._pageList[i];
            if(page.hasError())
            {
                error = true;
                break;
            }
        }

        return error;
    }

    // jsonに含めない
    // 前回開いた時 or 前回保存したときの内容のハッシュ
    public lastContentHash : string = "";

    // ハッシュをアップデートする
    public updateHash()
    {
        // openedFileNameはhashに含めない
        this.lastContentHash = this.calcHash();
        DebugHelper.trace("___last_hash : " + this.lastContentHash);
    }

    // 変更されたかどうか？
    public isModified()
    {
        var currentHash = this.calcHash();
        DebugHelper.trace("___last_hash : " + this.lastContentHash);
        DebugHelper.trace("current_hash : " + currentHash);
        return currentHash.toLowerCase() != this.lastContentHash.toLowerCase();
    }

    private calcHash()
    {
        // Hashに含めない情報をいったん逃してクリアする
        var fileVerstion = this.fileVersion;
        var fileEnvironment = this.fileEnvironment
        var opendFileName = this.openedFileName;
        this.fileVersion = 0;
        this.fileEnvironment = "";
        this.openedFileName = "";

        var hash = EncryptionHelper.calcHash(this);

        // 情報を元に戻す
        this.fileVersion = fileVerstion;
        this.fileEnvironment = fileEnvironment;
        this.openedFileName = opendFileName;

        return hash;
    }

}